import { useCallback, useEffect, useRef, useState } from 'react'
import { showNotification } from '@mantine/notifications'
import { api } from '@/lib/api'
import { playParallelTicketAlertSound } from '@/lib/parallel-ticket-alert-sound'
import { statusLabel, ticketPathSegmentFromListItem, ticketPublicId } from '@/lib/ticket-display'
import type { ParallelCustomerTicketItem, ParallelCustomerTicketsResponse } from '@/types/api'

type Params = {
  /** Loaded ticket UUID — avoid slug/route refs that fail lookup with many hyphens. */
  ticketId: string | undefined
  openOrSelectTab: (to: string) => void
}

export function useParallelCustomerTickets({ ticketId, openOrSelectTab }: Params) {
  const [data, setData] = useState<ParallelCustomerTicketsResponse | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const alertedForTicketRef = useRef<string | null>(null)

  useEffect(() => {
    setData(null)
    setDismissed(false)
    // Wait for the detail payload UUID; route slug-number can have >3 hyphens
    // and is rejected by find_ticket_by_id_or_slug.
    if (!ticketId) return

    let cancelled = false
    void api
      .parallelCustomerTickets(ticketId)
      .then((res) => {
        if (cancelled) return
        setData(res)
        if (!res.earliest || !res.tickets.length) return
        if (alertedForTicketRef.current === ticketId) return
        alertedForTicketRef.current = ticketId

        const hot = res.severity === 'hot'
        playParallelTicketAlertSound(hot ? 'hot' : 'warm')
        const earliest = res.earliest
        const publicId = ticketPublicId(earliest.project_slug, earliest.ticket_number)
        showNotification({
          id: `parallel-customer-${ticketId}`,
          title: hot
            ? 'У клиента уже есть открытое обращение'
            : 'У клиента есть другое активное обращение',
          message: `${publicId} (${statusLabel(earliest.status)}). Перейдите в самое раннее и продолжите там; текущее закройте вручную.`,
          color: hot ? 'red' : 'orange',
          autoClose: 12000,
        })
      })
      .catch(() => {
        if (!cancelled) setData(null)
      })

    return () => {
      cancelled = true
    }
  }, [ticketId])

  const goToEarliest = useCallback(
    (ticket: ParallelCustomerTicketItem) => {
      openOrSelectTab(`/tickets/${encodeURIComponent(ticketPathSegmentFromListItem(ticket))}`)
    },
    [openOrSelectTab],
  )

  const visible = Boolean(data?.earliest && data.tickets.length && !dismissed)

  return {
    parallelBanner: visible ? data : null,
    goToEarliest,
    dismissParallelBanner: () => setDismissed(true),
  }
}
